// SB6-ANA-DEF-01 Track 5.2 — global location-details by component group.
//
// Backs the "Location Detail" dropdown on the New-Defect drawer. The
// endpoint accepts either `componentGroupId` (UUID) or `componentGroupCode`
// (enum string). The FE consumes the code from the active data point's
// `componentGroupCode` (already on the DataPoint shape today), so we don't
// need a separate id-resolution call.
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type { ListLocationDetailsResponse } from "@aeriosiq/types";

export const locationDetailsKeys = {
  all: ["location-details"] as const,
  byCode: (code: string) =>
    [...locationDetailsKeys.all, "byCode", code] as const,
};

/**
 * Fetch the location-detail catalogue rows for a component group code.
 * Disabled when `componentGroupCode` is null — the drawer renders an empty
 * dropdown and falls through to a manual chord-% entry.
 */
export function useLocationDetailsByComponentGroupCode(
  componentGroupCode: string | null,
): UseQueryResult<ListLocationDetailsResponse> {
  const api = useApi();
  return useQuery<ListLocationDetailsResponse>({
    queryKey: locationDetailsKeys.byCode(componentGroupCode ?? ""),
    enabled: componentGroupCode != null && componentGroupCode.length > 0,
    // Catalogue data — rarely changes in-session.
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await api.get<ListLocationDetailsResponse>(
        "/location-details",
        {
          params: { componentGroupCode },
        },
      );
      return res.data;
    },
  });
}
